// The body diagram is a five-column grid keyed on the row and column of each hit location,
// with the sheet a tap on one opens.

import type { Caveat, PanelLocation } from "../vendor/web";
import { offeredAt } from "./coverage";
import type { Stall } from "./quality";
import { openSheet } from "./sheet";
import { shopList, type ShopSurface } from "./shop";

const COLUMNS = 5;

/** What the location sheet needs to sell. Adding a Browse facet here would let a search
 *  typed on another tab silently empty a location sheet. */
export interface LocationSurface extends ShopSurface {
  stalls: Stall[];
}

export type TapLocation = (location: PanelLocation) => void;

export function bodyDiagram(locations: PanelLocation[], tap?: TapLocation): HTMLElement {
  const grid = document.createElement("div");
  grid.className = "body-grid";

  let rows = 1;
  const cells = new Map<string, PanelLocation>();
  for (const location of locations) {
    rows = Math.max(rows, location.grid_row);
    cells.set(`${location.grid_row}-${location.grid_col}`, location);
  }
  grid.style.gridTemplateRows = `repeat(${rows}, minmax(3.25rem, auto))`;

  const open =
    tap ?? ((location: PanelLocation) => openSheet(location.name, locationDetail(location)));

  for (let row = 1; row <= rows; row += 1) {
    for (let column = 1; column <= COLUMNS; column += 1) {
      const location = cells.get(`${row}-${column}`);
      grid.append(location ? slotButton(location, open) : document.createElement("div"));
    }
  }
  return grid;
}

function slotButton(location: PanelLocation, tap: TapLocation): HTMLElement {
  const button = document.createElement("button");
  button.className = location.dr > 0 ? "slot slot-armoured" : "slot";
  button.setAttribute(
    "aria-label",
    `${location.name}, DR ${location.dr}${location.asterisk ? ", with damage-type caveats" : ""}`,
  );
  button.dataset["location"] = location.id;

  const name = document.createElement("span");
  name.className = "slot-name";
  name.textContent = location.name;

  const dr = document.createElement("span");
  dr.className = "slot-dr";
  dr.textContent = `DR ${location.dr}${location.asterisk ? "*" : ""}`;

  button.append(name, dr);
  button.addEventListener("click", () => tap(location));
  return button;
}

/** The whole sheet a tap opens: what covers the location now, then what could. */
export function locationSheet(location: PanelLocation, surface: LocationSurface): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "location-sheet";
  sheet.dataset["location"] = location.id;
  sheet.append(locationDetail(location), locationOffers(location, surface));
  return sheet;
}

export function locationOffers(location: PanelLocation, surface: LocationSurface): HTMLElement {
  const offers = offeredAt(surface.stalls, location.id);

  const wrapper = document.createElement("section");
  wrapper.className = "location-offers";

  const heading = document.createElement("h3");
  heading.textContent = "What can go here";
  wrapper.append(heading);

  if (offers.length === 0) {
    const empty = document.createElement("p");
    empty.className = "location-offers-empty";
    empty.textContent = `Nothing in the catalogue armours the ${location.name.toLowerCase()}.`;
    wrapper.append(empty);
    return wrapper;
  }

  const count = document.createElement("p");
  count.className = "location-offers-count";
  count.textContent = offers.length === 1 ? "1 row" : `${offers.length} rows`;
  wrapper.append(count, shopList(offers, surface));
  return wrapper;
}

export function locationDetail(location: PanelLocation): HTMLElement {
  const detail = document.createElement("div");
  detail.className = "location-detail";

  const headline = document.createElement("p");
  headline.className = "detail-headline";
  headline.textContent = `DR ${location.dr}`;
  detail.append(headline);

  if (location.caveats.length > 0) {
    detail.append(caveatList("Against", location.caveats));
  }

  if (location.inherent_dr > 0) {
    const inherent = document.createElement("p");
    inherent.className = "detail-inherent";
    inherent.textContent = `DR ${location.inherent_dr} of that is natural${
      location.inherent_caveats.length > 0 ? `, ${caveatText(location.inherent_caveats)}` : ""
    }`;
    detail.append(inherent);
  }

  if (location.layers.length > 0) {
    const heading = document.createElement("h3");
    heading.textContent = "Covered by";
    const list = document.createElement("ul");
    list.className = "detail-layers";
    for (const layer of location.layers) {
      const item = document.createElement("li");
      const label = document.createElement("span");
      label.textContent = layer.name;
      const value = document.createElement("span");
      value.className = "detail-layer-dr";
      value.textContent =
        layer.caveats.length > 0
          ? `DR ${layer.dr}, ${caveatText(layer.caveats)}`
          : `DR ${layer.dr}`;
      item.append(label, value);
      list.append(item);
    }
    detail.append(heading, list);
  }

  if (location.notes.length > 0) {
    const heading = document.createElement("h3");
    heading.textContent = "Notes";
    const list = document.createElement("ul");
    list.className = "detail-notes";
    for (const note of location.notes) {
      const item = document.createElement("li");
      item.textContent = `${note.source}: ${note.text}`;
      list.append(item);
    }
    detail.append(heading, list);
  }

  if (location.roll_range !== "" && location.roll_range !== "-") {
    const roll = document.createElement("p");
    roll.className = "detail-roll";
    roll.textContent = `Hit on ${location.roll_range}, at ${location.hit_penalty}`;
    detail.append(roll);
  }

  return detail;
}

function caveatList(heading: string, caveats: Caveat[]): HTMLElement {
  const wrapper = document.createElement("div");
  const title = document.createElement("h3");
  title.textContent = heading;
  const list = document.createElement("ul");
  list.className = "detail-caveats";
  for (const caveat of caveats) {
    const item = document.createElement("li");
    item.textContent = `${caveat.damage_type}: DR ${caveat.dr}`;
    list.append(item);
  }
  wrapper.append(title, list);
  return wrapper;
}

function caveatText(caveats: Caveat[]): string {
  return caveats.map((caveat) => `DR ${caveat.dr} vs ${caveat.damage_type}`).join(", ");
}
